import { Button, EmptyState } from '@personachat/ui';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { Coins, Download, FileDown, Filter, UserX, UsersRound } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';

import { api } from '../../api/client';
import { lire, messageDe } from '../../api/call';
import type { components, paths } from '../../api/schema';
import { DataTable, type Colonne } from '../../components/DataTable';
import { ErreurChargement, Filtre, Recherche, Select, StatutBadge, TypeBadge } from '../../components/bits';
import { ConfirmDialog } from '../../components/Modal';
import { useToast } from '../../components/Toast';
import { csv, enregistrer, nomDate } from '../../lib/fichiers';
import { formatDate, formatDateTime, formatNombre } from '../../lib/format';
import { LIBELLE_TYPE, STATUT_ACCES, statutCompte } from '../../lib/labels';
import { useFiltresUrl } from '../../lib/urlState';
import { PageHeader } from '../../pages/PageHeader';
import { BulkCreditDialog } from '../credits/BulkCreditDialog';

type Eleve = components['schemas']['UserListItem'];
type RequeteListe = paths['/api/admin/users']['get']['parameters']['query'];

const FILTRES = {
  recherche: '',
  type: 'all',
  statut: 'all',
  verifie: 'all',
  solde: 'all',
  inscription: 'all',
  connexion: 'all',
  acces: 'all',
  tri: 'createdAt',
  ordre: 'desc',
  page: '1',
};

type Filtres = typeof FILTRES;

const JOUR_MS = 24 * 3600 * 1000;
const ilYa = (jours: number) => new Date(Date.now() - jours * JOUR_MS).toISOString();

/** Filtres de l'écran → paramètres de GET /api/admin/users (dates calculées au moment de l'appel). */
export function requeteEleves(f: Filtres, limit = 25): NonNullable<RequeteListe> {
  const q: NonNullable<RequeteListe> = {
    page: Number(f.page) || 1,
    limit,
    sort: f.tri as NonNullable<RequeteListe>['sort'],
    order: f.ordre as 'asc' | 'desc',
  };
  if (f.recherche.trim()) q.search = f.recherche.trim();
  if (f.type !== 'all') q.userType = f.type as NonNullable<RequeteListe>['userType'];
  if (f.statut !== 'all') q.status = f.statut as NonNullable<RequeteListe>['status'];
  if (f.verifie !== 'all') q.emailVerified = f.verifie as 'true' | 'false';
  if (f.solde !== 'all') q.balance = f.solde as 'zero' | 'positive';
  if (f.inscription !== 'all') q.createdFrom = ilYa(Number(f.inscription));
  if (f.connexion === 'jamais') q.neverLoggedIn = 'true';
  else if (f.connexion === 'plus30') q.lastLoginTo = ilYa(30);
  else if (f.connexion !== 'all') q.lastLoginFrom = ilYa(Number(f.connexion));
  if (f.acces !== 'all') q.access = f.acces as NonNullable<RequeteListe>['access'];
  return q;
}

/**
 * Élèves (cahier des charges §7.2) : retrouver une élève en quelques
 * secondes, filtres gardés dans l'URL, fiche en panneau latéral (/eleves/:id).
 */
export function StudentsPage() {
  const { valeurs: f, modifier, reinitialiser, actifs } = useFiltresUrl(FILTRES);
  const navigate = useNavigate();
  const location = useLocation();
  const { id: ouvert } = useParams();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [selection, setSelection] = useState<Map<string, Eleve>>(new Map());
  const [ajoutGroupe, setAjoutGroupe] = useState(false);
  const [ajoutFait, setAjoutFait] = useState(false);
  const [desactivation, setDesactivation] = useState(false);
  const [export_, setExport] = useState(false);

  const cleFiltres = useMemo(() => ({ ...f, page: undefined }), [f]);
  // Une nouvelle recherche vide la sélection : elle portait sur une autre liste.
  useEffect(() => setSelection(new Map()), [JSON.stringify(cleFiltres)]);

  const liste = useQuery({
    queryKey: ['eleves', f],
    queryFn: () => lire(api.GET('/api/admin/users', { params: { query: requeteEleves(f) } })),
    placeholderData: keepPreviousData,
  });

  const ouvrir = (e: Eleve) => navigate(`/eleves/${e.id}${location.search}`);

  const colonnes: Colonne<Eleve>[] = [
    {
      id: 'eleve',
      header: 'Élève',
      sortKey: 'email',
      cell: (e) => (
        <div>
          <div className="cell-main">{e.fullName ?? e.username ?? '—'}</div>
          <div className="cell-sub">{e.email}</div>
        </div>
      ),
    },
    { id: 'type', header: 'Type', cell: (e) => <TypeBadge type={e.userType} isAdmin={e.isAdmin} /> },
    { id: 'statut', header: 'Statut', cell: (e) => <StatutBadge isActive={e.isActive} deletedAt={e.deletedAt} /> },
    {
      id: 'verifie',
      header: 'E-mail',
      secondaire: true,
      cell: (e) => (e.isEmailVerified ? <span className="muted">Vérifié</span> : <span className="neg">Non vérifié</span>),
    },
    {
      id: 'solde',
      header: 'Crédits',
      sortKey: 'balance',
      align: 'right',
      cell: (e) => <span className="num">{e.creditUnlimited ? 'Illimités' : formatNombre(e.creditBalance)}</span>,
    },
    { id: 'connexion', header: 'Dernière connexion', sortKey: 'lastLoginAt', cell: (e) => <span className="nowrap">{formatDateTime(e.lastLoginAt)}</span> },
    { id: 'inscription', header: 'Inscription', sortKey: 'createdAt', secondaire: true, cell: (e) => formatDate(e.createdAt) },
    {
      id: 'acces',
      header: 'Fin d’accès',
      sortKey: 'accessEndsAt',
      secondaire: true,
      cell: (e) =>
        e.access.status === 'NONE' ? (
          <span className="muted">—</span>
        ) : (
          <div>
            <div className="nowrap">{formatDate(e.access.endsAt)}</div>
            <div className="cell-sub">{STATUT_ACCES[e.access.status].libelle}</div>
          </div>
        ),
    },
    {
      id: 'cle',
      header: 'Clé d’accès',
      secondaire: true,
      cell: (e) =>
        e.accessKey ? (
          <div>
            <code className="small">{e.accessKey.key}</code>
            {e.accessKey.description && <div className="cell-sub">{e.accessKey.description}</div>}
          </div>
        ) : (
          '—'
        ),
    },
  ];

  const idsSelection = new Set(selection.keys());
  const destinatairesSelection = useMemo(() => ({ userIds: [...selection.keys()] }), [selection]);
  const changerSelection = (ids: Set<string>) => {
    const suivante = new Map<string, Eleve>();
    for (const id of ids) {
      const e = selection.get(id) ?? liste.data?.users.find((u) => u.id === id);
      if (e) suivante.set(id, e);
    }
    setSelection(suivante);
  };

  const selectionnees = [...selection.values()];
  const aDesactiver = selectionnees.filter((e) => e.isActive && !e.isAdmin && !e.deletedAt);

  const exporterSelection = () => {
    enregistrer(
      csv([
        ['email', 'nom', 'type', 'statut', 'solde_credits', 'derniere_connexion', 'inscription', 'acces_complet_depuis', 'fin_acces', 'etat_acces', 'cle_acces'],
        ...selectionnees.map((e) => [
          e.email,
          e.fullName,
          e.isAdmin ? 'Admin' : LIBELLE_TYPE[e.userType],
          statutCompte(e).libelle,
          e.creditUnlimited ? 'illimités' : e.creditBalance,
          e.lastLoginAt,
          e.createdAt,
          e.access.startedAt,
          e.access.endsAt,
          e.access.status === 'NONE' ? null : STATUT_ACCES[e.access.status].libelle,
          e.accessKey?.key,
        ]),
      ]),
      nomDate('eleves-selection')
    );
  };

  const exporterTout = async () => {
    setExport(true);
    try {
      const { page: _page, limit: _limit, ...query } = requeteEleves(f);
      const fichier = await lire(api.GET('/api/admin/users/export', { params: { query }, parseAs: 'blob' }));
      enregistrer(fichier, nomDate('eleves'));
    } catch (err) {
      toast.erreur(messageDe(err));
    } finally {
      setExport(false);
    }
  };

  const desactiver = async () => {
    let faits = 0;
    const echecs: string[] = [];
    for (const e of aDesactiver) {
      try {
        await lire(api.PATCH('/api/admin/users/{id}/toggle-status', { params: { path: { id: e.id } } }));
        faits += 1;
      } catch (err) {
        echecs.push(`${e.email} : ${messageDe(err)}`);
      }
    }
    await queryClient.invalidateQueries({ queryKey: ['eleves'] });
    setSelection(new Map());
    if (echecs.length) throw new Error(`${faits} compte(s) désactivé(s). Échecs — ${echecs.join(' ; ')}`);
    toast.succes(`${faits} compte${faits > 1 ? 's' : ''} désactivé${faits > 1 ? 's' : ''}.`);
  };

  const total = liste.data?.pagination.totalCount;

  return (
    <>
      <PageHeader
        title="Élèves"
        lead={total !== undefined ? `${formatNombre(total)} compte${total > 1 ? 's' : ''} correspondant${total > 1 ? 's' : ''} aux filtres.` : 'Retrouver une élève et traiter sa demande depuis sa fiche.'}
        actions={
          <Button icon={<Download size={15} aria-hidden="true" />} onClick={() => void exporterTout()} loading={export_}>
            Exporter
          </Button>
        }
      />

      <div className="toolbar" role="search">
        <Recherche
          id="eleves-recherche"
          label="Rechercher une élève"
          valeur={f.recherche}
          onChange={(recherche) => modifier({ recherche })}
          placeholder="E-mail, nom ou code de clé"
        />
        <Filtre id="f-type" label="Type">
          <Select id="f-type" value={f.type} onChange={(e) => modifier({ type: e.target.value })}>
            <option value="all">Tous</option>
            <option value="NORMAL">Normal</option>
            <option value="GUEST">Invité</option>
            <option value="CHALLENGE3">Immersion 3</option>
            <option value="ADMIN">Admin</option>
          </Select>
        </Filtre>
        <Filtre id="f-statut" label="Statut">
          <Select id="f-statut" value={f.statut} onChange={(e) => modifier({ statut: e.target.value })}>
            <option value="all">Tous</option>
            <option value="active">Actif</option>
            <option value="inactive">Désactivé</option>
            <option value="deleted">Supprimé</option>
          </Select>
        </Filtre>
        <Filtre id="f-verifie" label="E-mail">
          <Select id="f-verifie" value={f.verifie} onChange={(e) => modifier({ verifie: e.target.value })}>
            <option value="all">Tous</option>
            <option value="true">Vérifié</option>
            <option value="false">Non vérifié</option>
          </Select>
        </Filtre>
        <Filtre id="f-solde" label="Crédits">
          <Select id="f-solde" value={f.solde} onChange={(e) => modifier({ solde: e.target.value })}>
            <option value="all">Tous</option>
            <option value="zero">Solde nul</option>
            <option value="positive">Solde positif</option>
          </Select>
        </Filtre>
        <Filtre id="f-inscription" label="Inscription">
          <Select id="f-inscription" value={f.inscription} onChange={(e) => modifier({ inscription: e.target.value })}>
            <option value="all">Toutes</option>
            <option value="7">7 derniers jours</option>
            <option value="30">30 derniers jours</option>
            <option value="90">90 derniers jours</option>
          </Select>
        </Filtre>
        <Filtre id="f-connexion" label="Dernière connexion">
          <Select id="f-connexion" value={f.connexion} onChange={(e) => modifier({ connexion: e.target.value })}>
            <option value="all">Toutes</option>
            <option value="7">Depuis 7 jours</option>
            <option value="30">Depuis 30 jours</option>
            <option value="plus30">Il y a plus de 30 jours</option>
            <option value="jamais">Jamais</option>
          </Select>
        </Filtre>
        <Filtre id="f-acces" label="Accès">
          <Select id="f-acces" value={f.acces} onChange={(e) => modifier({ acces: e.target.value })}>
            <option value="all">Tous</option>
            <option value="active">En cours</option>
            <option value="ending">Se termine dans 30 jours</option>
            <option value="extended">Prolongé 3 mois</option>
            <option value="ended">Terminé</option>
            <option value="none">Sans échéance</option>
          </Select>
        </Filtre>
        {actifs > 0 && (
          <Button variant="ghost" size="sm" icon={<Filter size={14} aria-hidden="true" />} onClick={reinitialiser}>
            Effacer les filtres ({actifs})
          </Button>
        )}
      </div>

      {liste.isError ? (
        <ErreurChargement erreur={liste.error} onReessayer={() => void liste.refetch()} />
      ) : (
        <DataTable
          libelle="Élèves"
          colonnes={colonnes}
          lignes={liste.data?.users}
          cleLigne={(e) => e.id}
          chargement={liste.isFetching && !liste.data}
          tri={{ cle: f.tri, ordre: f.ordre as 'asc' | 'desc' }}
          onTri={(t) => modifier({ tri: t.cle, ordre: t.ordre })}
          selection={idsSelection}
          onSelection={changerSelection}
          onOuvrir={ouvrir}
          estActive={(e) => e.id === ouvert}
          pagination={
            liste.data
              ? { page: liste.data.pagination.page, pages: liste.data.pagination.totalPages, total: liste.data.pagination.totalCount }
              : undefined
          }
          onPage={(page) => modifier({ page: String(page) })}
          vide={
            <EmptyState
              icon={<UsersRound size={28} aria-hidden="true" />}
              title="Aucune élève ne correspond"
              text={actifs > 0 || f.recherche ? 'Élargissez la recherche ou effacez les filtres.' : 'Les inscriptions apparaîtront ici.'}
              action={actifs > 0 ? <Button onClick={reinitialiser}>Effacer les filtres</Button> : undefined}
            />
          }
        />
      )}

      {selection.size > 0 && (
        <div className="selection-bar" role="region" aria-label="Actions sur la sélection">
          <span className="selection-bar__count">
            {formatNombre(selection.size)} élève{selection.size > 1 ? 's' : ''} sélectionnée{selection.size > 1 ? 's' : ''}
          </span>
          <Button size="sm" icon={<Coins size={14} aria-hidden="true" />} onClick={() => setAjoutGroupe(true)}>
            Ajouter des crédits
          </Button>
          <Button size="sm" icon={<UserX size={14} aria-hidden="true" />} onClick={() => setDesactivation(true)} disabled={aDesactiver.length === 0}>
            Désactiver
          </Button>
          <Button size="sm" icon={<FileDown size={14} aria-hidden="true" />} onClick={exporterSelection}>
            Exporter la sélection
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setSelection(new Map())}>
            Vider
          </Button>
        </div>
      )}

      <BulkCreditDialog
        open={ajoutGroupe}
        onOpenChange={(o) => {
          setAjoutGroupe(o);
          // La sélection est vidée à la fermeture, pas avant : le résultat reste lisible.
          if (!o && ajoutFait) {
            setSelection(new Map());
            setAjoutFait(false);
          }
        }}
        destinataires={destinatairesSelection}
        onTermine={() => {
          setAjoutFait(true);
          void queryClient.invalidateQueries({ queryKey: ['eleves'] });
        }}
      />

      <ConfirmDialog
        open={desactivation}
        onOpenChange={setDesactivation}
        title="Désactiver les comptes sélectionnés"
        confirmLabel={`Désactiver ${aDesactiver.length} compte${aDesactiver.length > 1 ? 's' : ''}`}
        tone="danger"
        onConfirm={desactiver}
      >
        <p>
          <strong>{formatNombre(aDesactiver.length)}</strong> compte{aDesactiver.length > 1 ? 's' : ''} actif
          {aDesactiver.length > 1 ? 's' : ''} ne pourront plus se connecter ; les sessions ouvertes s’arrêtent. Réactivable depuis la fiche.
        </p>
        {selectionnees.length > aDesactiver.length && (
          <p className="note">
            {selectionnees.length - aDesactiver.length} compte(s) ignoré(s) : déjà désactivés, supprimés ou administrateurs.
          </p>
        )}
      </ConfirmDialog>

      <Outlet />
    </>
  );
}
